import axios from 'axios';
import tokenService from './tokenService';
import { API_CONFIG } from './config';

// Log the base URL being used
console.log('%c🔧 API Client Configuration', 'background: #3b82f6; color: white; padding: 4px 8px; font-weight: bold;');
console.log('Base URL:', API_CONFIG.DYNAMICS_BASE_URL);
console.log('Using Proxy:', API_CONFIG.USE_PROXY);
if (API_CONFIG.USE_PROXY) {
  console.log('Target URL:', API_CONFIG.DYNAMICS_DIRECT_URL);
}

const Api = axios.create({
  baseURL: API_CONFIG.DYNAMICS_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Log instance configuration
console.log('Axios baseURL:', Api.defaults.baseURL);
console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

// Add request interceptor to include token
Api.interceptors.request.use(
  async (config) => {
    // Log the full request URL
    const fullUrl = config.baseURL ? `${config.baseURL}${config.url}` : config.url;
    console.log(`%c📤 API Request: ${config.method?.toUpperCase()} ${fullUrl}`, 'color: #3b82f6; font-weight: bold');
    console.log('Request config:', {
      baseURL: config.baseURL,
      url: config.url,
      method: config.method,
      params: config.params
    });

    try {
      // Get token from token service (will fetch or return cached valid token)
      const token = await tokenService.getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        console.log('✅ Token attached to request');
      }
    } catch (error) {
      console.error('❌ Error getting access token:', error);
      // Continue with request even if token fetch fails
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
Api.interceptors.response.use(
  (response) => {
    // Log successful API calls in development
    if (process.env.NODE_ENV === 'development') {
      console.log(
        `%c✅ API Success: ${response.config.method.toUpperCase()} ${response.config.url}`,
        'color: #10b981; font-weight: bold'
      );
    }
    return response;
  },
  async (error) => {
    const requestUrl = error.config?.url || 'unknown';
    const requestMethod = error.config?.method?.toUpperCase() || 'GET';

    if (error.response?.status === 401) {
      console.error(
        `%c⚠️ 401 Unauthorized: ${requestMethod} ${requestUrl}`,
        'color: #f59e0b; font-weight: bold'
      );
      console.log('%c🔄 Attempting to refresh token and retry...', 'color: #3b82f6; font-weight: bold');

      // Clear token and try to fetch a new one
      tokenService.clearToken();

      // Retry the request once with a fresh token
      const originalRequest = error.config;
      if (!originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const newToken = await tokenService.getAccessToken();
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          console.log('%c✅ Retrying request with new token', 'color: #10b981; font-weight: bold');
          return Api(originalRequest);
        } catch (retryError) {
          console.error('%c❌ Failed to retry request with new token', 'color: #ef4444; font-weight: bold', retryError);
        }
      }
    } else if (error.response) {
      // Server responded with error
      console.error(
        `%c❌ API Error: ${requestMethod} ${requestUrl} - Status ${error.response.status}`,
        'color: #ef4444; font-weight: bold'
      );
      console.error('Error details:', error.response.data);
    } else if (error.request) {
      // Request made but no response
      console.error(
        `%c❌ Network Error: ${requestMethod} ${requestUrl}`,
        'color: #ef4444; font-weight: bold'
      );
      console.error('No response received from server');
    } else {
      // Something else happened
      console.error(`%c❌ Request Error: ${error.message}`, 'color: #ef4444; font-weight: bold');
    }

    return Promise.reject(error);
  }
);

export default Api;
